<template>
    <div>
        <h2 class="mb-2">{{ $t('clinic_prefs_edit.custom_categories') }}</h2>
        <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.custom_categories_description') }}</p>

        <v-card outlined class="pa-4 mb-4">
            <v-card-title class="px-0 py-2 d-flex justify-space-between align-center">
                <h4>{{ $t('clinic_prefs_edit.manage_categories') }}</h4>
                <v-btn color="primary" small @click="add_new_category" prepend-icon="mdi-plus">
                    {{ $t('clinic_prefs_edit.add_category') }}
                </v-btn>
            </v-card-title>
            <v-card-text class="px-0 py-2">
                <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.custom_categories_help') }}</p>

                <!-- Categories List -->
                <div v-if="!value || value.length === 0" class="text-center pa-4">
                    <v-icon size="64" color="grey lighten-1">mdi-folder-outline</v-icon>
                    <p class="text-h6 grey--text mt-2">{{ $t('clinic_prefs_edit.no_categories') }}</p>
                    <p class="text-caption grey--text">{{ $t('clinic_prefs_edit.no_categories_help') }}</p>
                </div>

                <v-list v-else>
                    <v-list-item v-for="(category, index) in sorted_custom_categories" :key="category.id" class="mb-2">
                        <v-list-item-avatar>
                            <v-icon>{{ category.icon }}</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>{{ category.name }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="edit_category(category)">
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </v-list-item-action>
                        <v-list-item-action>
                            <v-btn icon small color="error" @click="delete_category(category)">
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </v-card-text>
        </v-card>

        <!-- Category Edit Dialog -->
        <v-dialog v-model="category_dialog" max-width="500px" persistent>
            <v-card v-if="category_dialog">
                <v-card-title>
                    <span v-if="editing_category_index === null">{{ $t('clinic_prefs_edit.add_category') }}</span>
                    <span v-else>{{ $t('clinic_prefs_edit.edit_category') }}</span>
                </v-card-title>
                <v-card-text>
                    <v-alert v-if="error_message" outlined type="error" :value="error_message" class="mb-4">
                        {{ error_message }}
                    </v-alert>
                    <v-text-field v-model="editing_category.name" :label="$t('clinic_prefs_edit.category_name')"
                        outlined required />
                    <v-autocomplete v-model="editing_category.icon" :label="$t('clinic_prefs_edit.category_icon')"
                        :items="display_icons" outlined required clearable :loading="icons_loading"
                        :search-input.sync="icon_search_query" :filter="() => true"
                        :no-data-text="icons_loading ? 'Loading...' : 'No icons found'" append-icon="mdi-magnify"
                        @update:search-input="on_icon_search_change" @keydown.enter="on_icon_search_enter">
                        <template v-slot:item="data">
                            <v-list-item-content>
                                <v-list-item-title>
                                    <v-icon class="mr-2">{{ data.item.value }}</v-icon>
                                    {{ data.item.text }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </template>
                        <template v-slot:selection="data">
                            <v-icon class="mr-2">{{ data.item.value }}</v-icon>
                            {{ data.item.text }}
                        </template>
                        <template v-slot:append-item>
                            <div v-if="!icon_search_query && !icons_loading" v-intersect="on_intersect_load_more"
                                class="pa-2 text-center">
                                <v-progress-circular v-if="loading_more_icons" indeterminate size="20"
                                    width="2"></v-progress-circular>
                                <span v-else class="text-caption grey--text">Scroll for more...</span>
                            </div>
                        </template>
                    </v-autocomplete>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancel_category_edit">
                        {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn color="primary" @click="save_category"
                        :disabled="!editing_category.name || !editing_category.icon">
                        {{ $t('common.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';

export default {
    name: 'ClinicPrefsEditCategories',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        clinic_id: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            // Category management
            category_dialog: false,
            editing_category_index: null,
            editing_category: {
                id: null,
                name: '',
                icon: ''
            },
            error_message: '',

            // Icon search and loading
            available_icons: [],
            icons_loading: false,
            loading_more_icons: false,
            icon_search_query: '',
            icon_search_debounce_timer: null,
            icon_browse_offset: 0,
            icon_browse_limit: 20,
            category_loading: false
        };
    },
    computed: {
        sorted_custom_categories() {
            if (!this.value) {
                return [];
            }
            // Sort custom categories alphabetically using locale-aware sorting
            return [...this.value].sort((a, b) =>
                a.name.localeCompare(b.name, this.$i18n.locale)
            );
        },

        // Get the currently selected icon for display
        current_icon_item() {
            if (this.editing_category.icon) {
                return {
                    text: this.formatIconDisplayName(this.editing_category.icon),
                    value: this.editing_category.icon,
                    is_current: true
                };
            }
            return null;
        },

        // Combine current icon with available icons, ensuring current icon is first and deduplicated
        display_icons() {
            const current = this.current_icon_item;
            const available = this.available_icons;

            if (!current) {
                return available;
            }

            // Filter out the current icon from available icons to avoid duplication
            const filtered = available.filter(icon => icon.value !== current.value);

            // Return current icon first, then filtered available icons
            return [current, ...filtered];
        }
    },
    methods: {
        // Icon search and loading methods
        async load_icons(query = null, limit = null, offset = null) {            
            this.icons_loading = true;

            try {
                const params = new URLSearchParams();
                if (query) {
                    params.append('query', query);
                    params.append('limit', limit || 10);
                } else {
                    params.append('limit', limit || this.icon_browse_limit);
                    params.append('offset', offset || this.icon_browse_offset);
                }

                const response = await axios.get(`/api/clinic/${this.clinic_id}/prefs/icons/search?${params.toString()}`);

                // The API returns a direct array of strings, not wrapped in status/data
                const icons = response.data.data || [];

                if (query) {
                    // Search mode - replace all icons
                    this.available_icons = icons.map(icon => ({
                        text: this.formatIconDisplayName(icon),
                        value: icon
                    }));
                } else {
                    // Browse mode - append icons
                    const new_icons = icons.map(icon => ({
                        text: this.formatIconDisplayName(icon),
                        value: icon
                    }));

                    this.available_icons = [...this.available_icons, ...new_icons];

                    // For browse mode, only update offset if we actually received new icons
                    // This prevents infinite loading when there are no more icons
                    if (icons.length > 0) {
                        this.icon_browse_offset = this.available_icons.length;
                    }
                }

                // Ensure current icon is always available if we have one
                this.ensure_current_icon_loaded();
                
                // Reset category_loading flag on successful search result
                this.category_loading = false;
            } catch (error) {
                console.error('Error loading icons:', error);
                this.error_message = 'Failed to load icons. Please try again.';
                // Reset category_loading flag on error too
                this.category_loading = false;
            } finally {
                this.icons_loading = false;
                this.loading_more_icons = false;
            }
        },

        on_icon_search_change(query) {
            // Don't trigger search if we're loading a category
            if (this.category_loading) {
                return;
            }

            // Clear existing debounce timer
            if (this.icon_search_debounce_timer) {
                clearTimeout(this.icon_search_debounce_timer);
            }

            // Set new debounce timer
            this.icon_search_debounce_timer = setTimeout(() => {
                if (query && query.trim()) {
                    // Search mode
                    this.load_icons(query.trim(), 10, 0);
                } else {
                    // Browse mode - reset and load first page
                    this.icon_browse_offset = 0;
                    this.available_icons = []; // Clear existing search results
                    this.load_icons(null, this.icon_browse_limit, 0);
                }
            }, 500);
        },

        on_intersect_load_more(entries, observer, isIntersecting) {
            // Load more icons when the append-item becomes visible (browse mode only)
            if (isIntersecting && !this.icon_search_query && !this.icons_loading && !this.loading_more_icons) {
                this.loading_more_icons = true;
                this.load_icons(null, this.icon_browse_limit, this.icon_browse_offset);
            }
        },

        ensure_current_icon_loaded() {
            // If we have a current icon and it's not in the available icons, add it
            if (this.editing_category.icon && !this.available_icons.find(icon => icon.value === this.editing_category.icon)) {
                const current_icon = {
                    text: this.formatIconDisplayName(this.editing_category.icon),
                    value: this.editing_category.icon
                };
                // Add to the beginning of the list
                this.available_icons.unshift(current_icon);
            }
        },

        on_icon_search_enter(event) {
            // Immediately search when Enter is pressed, bypassing debounce
            const query = this.icon_search_query;
            if (query && query.trim()) {
                // Clear any pending debounce timer
                if (this.icon_search_debounce_timer) {
                    clearTimeout(this.icon_search_debounce_timer);
                }
                // Search mode with 10 items limit
                this.load_icons(query.trim(), 10, 0);
            }
        },

        // Category management methods
        add_new_category() {
            this.editing_category_index = null;
            this.editing_category = {
                id: uuidv4(),
                name: '',
                icon: ''
            };
            this.error_message = '';
            this.icon_search_query = '';
            this.category_dialog = true;
        },

        edit_category(category) {
            // Clear any existing search context and loaded icons
            this.icon_search_query = '';
            if (this.icon_search_debounce_timer) {
                clearTimeout(this.icon_search_debounce_timer);
            }
            this.available_icons = [];
            this.icon_browse_offset = 0;
            this.icons_loading = false;
            this.loading_more_icons = false;
            this.icon_search_debounce_timer = null;

            // Find the index in the original array by category ID
            const index = this.value.findIndex(cat => cat.id === category.id);
            if (index === -1) return;

            this.editing_category_index = index;
            this.editing_category = {
                id: this.value[index].id,
                name: this.value[index].name,
                icon: this.value[index].icon
            };
            this.error_message = '';

            Vue.nextTick(() => {
                // Set category_loading flag to prevent on_icon_search_change from triggering
                this.category_loading = true;
                
                // If we have an existing icon, search for related icons
                this.ensure_current_icon_loaded();
                if (this.editing_category.icon) {
                    const iconDisplayName = this.formatIconDisplayName(this.editing_category.icon);
                    this.icon_search_query = iconDisplayName;
                    // Trigger search for related icons
                    this.load_icons(iconDisplayName, 10, 0);
                } else {
                    this.icon_search_query = '';
                    // Load initial icons for browsing
                    this.load_icons(null, this.icon_browse_limit, 0);
                }

                this.category_dialog = true;
            });
        },

        delete_category(category) {
            // Find the index in the original array by category ID
            const index = this.value.findIndex(cat => cat.id === category.id);
            if (index === -1) return;

            if (confirm(this.$t('clinic_prefs_edit.confirm_delete_category', { name: this.value[index].name }))) {
                const updatedCategories = [...this.value];
                updatedCategories.splice(index, 1);
                this.$emit('input', updatedCategories);
            }
        },

        save_category() {
            // Validate required fields
            if (!this.editing_category.name || !this.editing_category.name.trim()) {
                this.error_message = this.$t('clinic_prefs_edit.category_name_required');
                return;
            }

            if (!this.editing_category.icon || !this.editing_category.icon.trim()) {
                this.error_message = this.$t('clinic_prefs_edit.category_icon_required');
                return;
            }

            // Clear any previous error messages
            this.error_message = '';

            const currentCategories = this.value || [];
            const updatedCategories = [...currentCategories];

            if (this.editing_category_index === null) {
                // Adding new category
                updatedCategories.push({
                    id: this.editing_category.id,
                    name: this.editing_category.name,
                    icon: this.editing_category.icon
                });
            } else {
                // Editing existing category
                updatedCategories[this.editing_category_index] = {
                    id: this.editing_category.id,
                    name: this.editing_category.name,
                    icon: this.editing_category.icon
                };
            }

            this.$emit('input', updatedCategories);
            this.category_dialog = false;
            this.editing_category_index = null;
            this.editing_category = { id: null, name: '', icon: '' };
        },

        cancel_category_edit() {
            this.category_dialog = false;
            this.editing_category_index = null;
            this.editing_category = { id: null, name: '', icon: '' };
            this.error_message = '';
            this.icon_search_query = '';

            // Clear debounce timer
            if (this.icon_search_debounce_timer) {
                clearTimeout(this.icon_search_debounce_timer);
            }
        },

        formatIconDisplayName(iconId) {
            // Remove leading fa- or mdi- and replace dashes with spaces
            let displayName = iconId;
            if (displayName.startsWith('fa-')) {
                displayName = displayName.substring(3);
            } else if (displayName.startsWith('mdi-')) {
                displayName = displayName.substring(4);
            }
            return displayName.replace(/-/g, ' ');
        },
    },
};
</script>
